var id = 1;
var getId = function () { return "webapp-".concat(id++); };

var BRIDGE = '__tuenti_webview_bridge';
var hasAndroidPostMessage = function () {
    return !!(typeof window !== 'undefined' &&
        window.tuentiWebView &&
        window.tuentiWebView.postMessage);
};
var hasWebKitPostMessage = function () {
    return !!(typeof window !== 'undefined' &&
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.tuentiWebView &&
        window.webkit.messageHandlers.tuentiWebView.postMessage);
};
/**
 * Maybe returns postMessage function exposed by native apps
 */
var getWebViewPostMessage = function () {
    if (typeof window === 'undefined') {
        return null;
    }
    // Android
    if (hasAndroidPostMessage()) {
        return function (jsonMessage) {
            window.tuentiWebView.postMessage(jsonMessage);
        };
    }
    // iOS
    if (hasWebKitPostMessage()) {
        return function (jsonMessage) {
            window.webkit.messageHandlers.tuentiWebView.postMessage(jsonMessage);
        };
    }
    return null;
};
var messageListeners = [];
var subscribe = function (listener) {
    messageListeners.push(listener);
};
var unsubscribe = function (listener) {
    messageListeners = messageListeners.filter(function (f) { return f !== listener; });
};
var isInIframe = function () {
    try {
        return window.self !== window.top;
    }
    catch (e) {
        return true;
    }
};
var isDisabledFromIframe = function () {
    var _a;
    if (typeof window === 'undefined') {
        return false;
    }
    if (!isInIframe()) {
        return false;
    }
    return !((_a = window === null || window === void 0 ? void 0 : window.frameElement) === null || _a === void 0 ? void 0 : _a.hasAttribute('data-enable-webview-bridge'));
};
/**
 * Returns true if there is a WebView Bridge installed
 */
var isWebViewBridgeAvailable = function () {
    return !isDisabledFromIframe() &&
        (hasAndroidPostMessage() || hasWebKitPostMessage());
};
/**
 * Send message to native app and waits for response
 */
var postMessageToNativeApp = function (_a, timeout) {
    var type = _a.type, _b = _a.id, id = _b === void 0 ? getId() : _b, payload = _a.payload;
    var postMessage = getWebViewPostMessage();
    var message = JSON.stringify({ type: type, id: id, payload: payload });
    if (!postMessage) {
        return Promise.reject({
            code: 500,
            reason: 'WebView postMessage not available',
        });
    }
    // ensure postMessage call is async
    setTimeout(function () {
        postMessage(message);
    });
    return new Promise(function (resolve, reject) {
        var timedOut = false;
        var listener = function (response) {
            if (response.id === id && !timedOut) {
                if (response.type === type) {
                    resolve(response.payload);
                }
                else if (response.type === 'ERROR') {
                    reject(response.payload);
                }
                else {
                    reject({
                        code: 500,
                        reason: "bad type: ".concat(response.type, ". Expecting ").concat(type),
                    });
                }
                unsubscribe(listener);
            }
        };
        subscribe(listener);
        if (timeout) {
            setTimeout(function () {
                timedOut = true;
                unsubscribe(listener);
                reject({ code: 408, reason: 'request timeout' });
            }, timeout);
        }
    });
};
/**
 * Initiates WebApp postMessage function, which will be called by native apps
 */
if (typeof window !== 'undefined') {
    window[BRIDGE] = window[BRIDGE] || {
        postMessage: function (jsonMessage) {
            var message;
            try {
                message = JSON.parse(jsonMessage);
            }
            catch (e) {
                throw Error("Problem parsing webview message: ".concat(jsonMessage));
            }
            messageListeners.forEach(function (f) { return f(message); });
        },
    };
}
var listenToNativeMessage = function (type, handler) {
    var listener = function (message) {
        if (message.type === type) {
            Promise.resolve(handler(message.payload)).then(function (responsePayload) {
                var postMessage = getWebViewPostMessage();
                if (postMessage) {
                    postMessage(JSON.stringify({
                        type: message.type,
                        id: message.id,
                        payload: responsePayload,
                    }));
                }
            });
        }
    };
    subscribe(listener);
    return function () {
        unsubscribe(listener);
    };
};
var onNativeEvent = function (eventHandler) {
    var handler = function (payload) {
        var response = eventHandler({
            event: payload.event,
        });
        return {
            action: response.action || 'default',
        };
    };
    return listenToNativeMessage('NATIVE_EVENT', handler);
};

var nativeConfirm = function (_a) {
    var message = _a.message, title = _a.title, acceptText = _a.acceptText, cancelText = _a.cancelText;
    if (isWebViewBridgeAvailable()) {
        return postMessageToNativeApp({
            type: 'CONFIRM',
            payload: { message: message, title: title, acceptText: acceptText, cancelText: cancelText },
        }).then(function (_a) {
            var result = _a.result;
            return result;
        });
    }
    else {
        return Promise.resolve(typeof window === 'undefined' ? false : window.confirm(message));
    }
};
var nativeAlert = function (_a) {
    var message = _a.message, title = _a.title, buttonText = _a.buttonText;
    if (isWebViewBridgeAvailable()) {
        return postMessageToNativeApp({
            type: 'ALERT',
            payload: { title: title, message: message, buttonText: buttonText },
        });
    }
    else {
        if (typeof window !== 'undefined') {
            window.alert(message);
        }
        return Promise.resolve();
    }
};
var nativeMessage = function (_a) {
    var message = _a.message, duration = _a.duration, buttonText = _a.buttonText, type = _a.type;
    if (isWebViewBridgeAvailable()) {
        return postMessageToNativeApp({
            type: 'MESSAGE',
            payload: { message: message, duration: duration, buttonText: buttonText, type: type },
        });
    }
    else {
        if (typeof window !== 'undefined') {
            window.alert(message);
        }
        return Promise.resolve();
    }
};

var TIMEOUT = 200;
var requestSimIcc = function () {
    return postMessageToNativeApp({ type: 'SIM_ICC' }, TIMEOUT)
        .then(function (_a) {
        var icc = _a.icc;
        return icc;
    })
        .catch(function () { return null; });
};
var requestSimImsi = function () {
    return postMessageToNativeApp({ type: 'IMSI' }, TIMEOUT)
        .then(function (_a) {
        var imsi = _a.imsi;
        return imsi;
    })
        .catch(function () { return null; });
};
var requestDeviceImei = function () {
    return postMessageToNativeApp({ type: 'IMEI' }, TIMEOUT)
        .then(function (_a) {
        var imei = _a.imei;
        return imei;
    })
        .catch(function () { return null; });
};
var internalNavigation = function (feature) {
    return postMessageToNativeApp({
        type: 'INTERNAL_NAVIGATION',
        payload: {
            feature: feature,
        },
    });
};
var dismiss = function (onCompletionUrl) {
    return postMessageToNativeApp({
        type: 'DISMISS',
        payload: {
            onCompletionUrl: onCompletionUrl,
        },
    });
};
var requestVibration = function (type) {
    return postMessageToNativeApp({
        type: 'VIBRATION',
        payload: {
            type: type,
        },
    });
};
var getDiskSpaceInfo = function () {
    return postMessageToNativeApp({
        type: 'GET_DISK_SPACE_INFO',
    });
};
var getEsimInfo = function () {
    return postMessageToNativeApp({
        type: 'GET_ESIM_INFO',
    }).catch(function () { return ({
        supportsEsim: false,
    }); });
};

var attachToEmail = function (_a) {
    var url = _a.url, subject = _a.subject, fileName = _a.fileName, recipient = _a.recipient, body = _a.body;
    return postMessageToNativeApp({
        type: 'ATTACH_TO_EMAIL',
        payload: { url: url, subject: subject, fileName: fileName, recipient: recipient, body: body },
    });
};
var share = function (options) {
    return postMessageToNativeApp({
        type: 'SHARE',
        payload: options,
    });
};
var updateNavigationBar = function (_a) {
    var title = _a.title, expandedTitle = _a.expandedTitle, showBackButton = _a.showBackButton, showReloadButton = _a.showReloadButton, showProfileButton = _a.showProfileButton, backgroundColor = _a.backgroundColor;
    if (isWebViewBridgeAvailable()) {
        return postMessageToNativeApp({
            type: 'NAVIGATION_BAR',
            payload: {
                title: title,
                expandedTitle: expandedTitle,
                showBackButton: showBackButton,
                showReloadButton: showReloadButton,
                showProfileButton: showProfileButton,
                backgroundColor: backgroundColor,
            },
        });
    }
    else {
        if (typeof title !== 'undefined' && typeof document !== 'undefined') {
            document.title = title;
        }
        return Promise.resolve();
    }
};
/**
 * @deprecated
 */
var setWebViewTitle = function (title) {
    if (isWebViewBridgeAvailable()) {
        return updateNavigationBar({ title: title });
    }
    else {
        if (typeof document !== 'undefined') {
            document.title = title;
        }
        return Promise.resolve();
    }
};
var notifyPageLoaded = function () {
    return postMessageToNativeApp({ type: 'PAGE_LOADED' });
};
var remoteConfig = null;
var isRemoteConfigAvailable = function (key) {
    return remoteConfig.result[key] === 'true';
};
var isABTestingAvailable = function (key) {
    if (!remoteConfig) {
        // If GET_REMOTE_CONFIG takes more than 5s to respond, resolve to false
        var timeoutP = new Promise(function (resolve) {
            setTimeout(function () {
                resolve(false);
            }, 500);
        });
        var configP = postMessageToNativeApp({
            type: 'GET_REMOTE_CONFIG',
        }).then(function (res) {
            remoteConfig = res;
            return isRemoteConfigAvailable(key);
        });
        return Promise.race([timeoutP, configP]);
    }
    else {
        return Promise.resolve(isRemoteConfigAvailable(key));
    }
};
var reportStatus = function (_a) {
    var feature = _a.feature, status = _a.status, reason = _a.reason;
    return postMessageToNativeApp({
        type: 'STATUS_REPORT',
        payload: { feature: feature, status: status, reason: reason },
    });
};
var fetch = function (_a) {
    var url = _a.url, method = _a.method, headers = _a.headers, body = _a.body;
    if (isWebViewBridgeAvailable()) {
        return postMessageToNativeApp({
            type: 'FETCH',
            payload: { url: url, method: method, headers: headers, body: body },
        }).catch(function () { return ({
            status: 500,
            headers: {},
            body: 'Bridge call failed',
        }); });
    }
    return Promise.resolve({
        status: 500,
        headers: {},
        body: 'Bridge not available',
    });
};
var checkPermissionStatus = function (feature, params) {
    return postMessageToNativeApp({
        type: 'OS_PERMISSION_STATUS',
        payload: {
            feature: feature,
            params: params,
        },
    }).then(function (_a) {
        var granted = _a.granted;
        return granted;
    });
};
var getAppMetadata = function (appToken) {
    return postMessageToNativeApp({
        type: 'GET_APP_METADATA',
        payload: {
            appToken: appToken,
        },
    });
};
var setActionBehavior = function (actions) {
    return postMessageToNativeApp({
        type: 'SET_ACTION_BEHAVIOR',
        payload: {
            actions: actions,
        },
    }).catch(function () {
        // do nothing
    });
};

var msToS = function (ms) { return Math.floor(ms / 1000); };
var createCalendarEvent = function (_a) {
    var beginTime = _a.beginTime, endTime = _a.endTime, title = _a.title;
    return postMessageToNativeApp({
        type: 'CREATE_CALENDAR_EVENT',
        payload: {
            beginTime: msToS(beginTime),
            endTime: msToS(endTime),
            title: title,
        },
    });
};

var requestContact = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.filter, filter = _c === void 0 ? 'phone' : _c;
    return postMessageToNativeApp({ type: 'GET_CONTACT_DATA', payload: { filter: filter } });
};
var fetchContactsByPhone = function (phoneNumbers) {
    return postMessageToNativeApp({
        type: 'FETCH_CONTACTS_DATA',
        payload: { phoneNumbers: phoneNumbers },
    });
};

var highlightNavigationTab = function (_a) {
    var tab = _a.tab, highlight = _a.highlight, count = _a.count;
    return postMessageToNativeApp({
        type: 'HIGHLIGHT_TAB',
        payload: {
            tab: tab,
            highlight: highlight,
            count: count,
        },
    });
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var CD_OB_IDS = 1; // from sessionInfo
var CD_PAYMENT_MODELS = 2; // from sessionInfo
var CD_SERVICE_WORKER_STATUS = 3;
var CD_WEBAPP_INSTALLED = 4;
var CD_SUBSCRIPTION_ADMIN = 5; // from sessionInfo
var CD_SUBSCRIPTION_WITH_IPCOMMS = 6; // from sessionInfo
var CD_NOVUM_UID = 7;
var CD_EVENT_VALUE = 8;
var CD_AF_SOURCE = 9;
var CD_AF_CAMPAIGN = 10;
var CD_NOVUM_UID_SESSION = 11;
var CD_USER_LOGGED = 12;
var CD_CURRENT_SUBSCRIPTION_ID = 13;
var CD_CURRENT_SUBSCRIPTION_TYPE = 14;
var CD_CURRENT_PAYMENT_MODEL = 15;
var CD_WEBVIEW_BROWSER_VERSION = 16;
var CD_ACTIVATED_ROLES = 17;
var CD_APP_INSTANCE_ID = 18;
var CD_EXPERIMENT_FLAG = 20;
var CD_FRIENDS_APPS = 24;
var CD_ACOUNT_LINE_SELECTOR = 25;
var CD_ONE_CLICK_DISPLAYED = 26;
var DEFAULT_EVENT_LABEL = 'null_label';
var DEFAULT_EVENT_VALUE = 0;
var VALID_TRACKERS = ['NovumTracker', 'OBARGTracker'];
var isTrackerValid = function (tracker) {
    return VALID_TRACKERS.indexOf(tracker.get('name')) >= 0;
};
var withAnalytics = function (_a) {
    var onAndroid = _a.onAndroid, onIos = _a.onIos, onWeb = _a.onWeb;
    if (typeof window === 'undefined') {
        return Promise.resolve();
    }
    if (window.AnalyticsWebInterface) {
        // Call Android interface
        return onAndroid(window.AnalyticsWebInterface);
    }
    else if (window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.firebase) {
        // Call iOS interface
        return onIos(window.webkit.messageHandlers.firebase);
    }
    else if (window.ga &&
        // @ts-ignore
        window.ga.loaded) {
        // Use Google Analytics when webapp is outside the native app webview
        return onWeb(window.ga);
    }
    else {
        return Promise.resolve();
    }
};
var formatLabel = function (label) {
    // Normalize to NFD (normal form decomposes and delete Combining Diacritical Marks Unicode
    // https://stackoverflow.com/a/37511463/3874587
    return label.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};
var logEvent = function (_a) {
    var category = _a.category, action = _a.action, label = _a.label, value = _a.value, fieldsObject = __rest(_a, ["category", "action", "label", "value"]);
    if (!category || !action) {
        console.warn('Analytics event should have "category" and "action"', {
            category: category,
            action: action,
        });
        return Promise.resolve();
    }
    var name = category;
    if (!label) {
        label = DEFAULT_EVENT_LABEL;
    }
    if (!value) {
        value = DEFAULT_EVENT_VALUE;
    }
    var params = __assign({ eventCategory: category, eventAction: action, eventLabel: formatLabel(label), eventValue: value }, fieldsObject);
    return withAnalytics({
        onAndroid: function (androidFirebase) {
            if (androidFirebase.logEvent) {
                androidFirebase.logEvent(name, JSON.stringify(params));
            }
            return Promise.resolve();
        },
        onIos: function (iosFirebase) {
            iosFirebase.postMessage({
                command: 'logEvent',
                name: name,
                parameters: params,
            });
            return Promise.resolve();
        },
        onWeb: function (ga) {
            return new Promise(function (resolve) {
                ga('NovumTracker.send', 'event', __assign(__assign({}, params), { hitCallback: resolve }));
            });
        },
    });
};
var logEcommerceEvent = function (name, params) {
    return withAnalytics({
        onAndroid: function (androidFirebase) {
            if (androidFirebase.logEvent) {
                androidFirebase.logEvent(name, JSON.stringify(params));
            }
            return Promise.resolve();
        },
        onIos: function (iosFirebase) {
            iosFirebase.postMessage({
                command: 'logEvent',
                name: name,
                parameters: params,
            });
            return Promise.resolve();
        },
        onWeb: function () {
            // not implemented on web
            return Promise.resolve();
        },
    });
};
var logTiming = function (_a) {
    var _b = _a.category, category = _b === void 0 ? 'performance_timer' : _b, variable = _a.variable, value = _a.value, label = _a.label;
    if (!category || !variable || !value) {
        console.warn('Analytics timing should have "category", "variable" and "value"', { category: category, variable: variable, value: value });
        return Promise.resolve();
    }
    value = Math.round(value);
    var params = {
        timingCategory: category,
        timingVar: variable,
        timingValue: value,
        timingLabel: label,
    };
    var name = category;
    return withAnalytics({
        onAndroid: function (androidFirebase) {
            if (androidFirebase.logEvent) {
                androidFirebase.logEvent(name, JSON.stringify(params));
            }
            return Promise.resolve();
        },
        onIos: function (iosFirebase) {
            iosFirebase.postMessage({
                command: 'logEvent',
                name: name,
                parameters: params,
            });
            return Promise.resolve();
        },
        onWeb: function (ga) {
            return new Promise(function (resolve) {
                var _a;
                ga('NovumTracker.send', __assign((_a = { hitType: 'timing', hitCallback: resolve }, _a["dimension".concat(CD_EVENT_VALUE)] = String(value), _a), params));
            });
        },
    });
};
var currentPageName;
var setScreenName = function (screenName, fieldsObject) {
    if (!screenName) {
        console.warn('Missing analytics screenName');
        return Promise.resolve();
    }
    return withAnalytics({
        onAndroid: function (androidFirebase) {
            if (androidFirebase.setScreenName) {
                androidFirebase.setScreenName(screenName);
            }
            return Promise.resolve();
        },
        onIos: function (iosFirebase) {
            iosFirebase.postMessage({
                command: 'setScreenName',
                name: screenName,
            });
            return Promise.resolve();
        },
        onWeb: function (ga) {
            return new Promise(function (resolve) {
                // Page name should start with '/'
                var pageName = screenName.startsWith('/')
                    ? screenName
                    : "/".concat(screenName);
                if (pageName !== currentPageName) {
                    currentPageName = pageName;
                    ga(function () {
                        // we have two trackers in movistar ARG, we want to track the PV in both trackers
                        var trackers = ga.getAll().filter(isTrackerValid);
                        trackers.forEach(function (tracker) {
                            tracker.set('page', pageName);
                            tracker.send('pageView', __assign(__assign({}, fieldsObject), { hitCallback: resolve }));
                        });
                    });
                }
                else {
                    resolve();
                }
            });
        },
    });
};
var USER_PROPERTY_TO_CUSTOM_DIMENSION = {
    obIds: CD_OB_IDS,
    paymentModels: CD_PAYMENT_MODELS,
    serviceWorkerStatus: CD_SERVICE_WORKER_STATUS,
    isAdmin: CD_SUBSCRIPTION_ADMIN,
    hasIpComms: CD_SUBSCRIPTION_WITH_IPCOMMS,
    af_source: CD_AF_SOURCE,
    af_campaign: CD_AF_CAMPAIGN,
    novum_uid_session: CD_NOVUM_UID_SESSION,
    user_logged: CD_USER_LOGGED,
    currentSubscriptionId: CD_CURRENT_SUBSCRIPTION_ID,
    currentSubscriptionType: CD_CURRENT_SUBSCRIPTION_TYPE,
    currentPaymentModel: CD_CURRENT_PAYMENT_MODEL,
    webviewBrowserVersion: CD_WEBVIEW_BROWSER_VERSION,
    activatedRoles: CD_ACTIVATED_ROLES,
    appInstanceId: CD_APP_INSTANCE_ID,
    experimentflag: CD_EXPERIMENT_FLAG,
    friendsApps: CD_FRIENDS_APPS,
    accountLineSelector: CD_ACOUNT_LINE_SELECTOR,
    OneClickDisplayed: CD_ONE_CLICK_DISPLAYED,
};
var setUserProperty = function (name, value) {
    if (!name || !value) {
        console.warn('Trying to set analytics user property without name or value', name, value);
        return Promise.resolve();
    }
    value = String(value);
    return withAnalytics({
        onAndroid: function (androidFirebase) {
            if (androidFirebase.setUserProperty) {
                androidFirebase.setUserProperty(name, value);
            }
            return Promise.resolve();
        },
        onIos: function (iosFirebase) {
            iosFirebase.postMessage({
                command: 'setUserProperty',
                name: name,
                value: value,
            });
            return Promise.resolve();
        },
        onWeb: function (ga) {
            var _a;
            var dimensionIdx = USER_PROPERTY_TO_CUSTOM_DIMENSION[name];
            if (!dimensionIdx) {
                console.warn('No custom dimension defined for user property', name);
                return Promise.resolve();
            }
            ga('NovumTracker.set', (_a = {},
                _a["dimension".concat(dimensionIdx)] = String(value),
                _a));
            return Promise.resolve();
        },
    });
};
var setCustomerHash = function (hash) {
    return postMessageToNativeApp({
        type: 'SET_CUSTOMER_HASH',
        payload: {
            hash: hash,
        },
    });
};
var getCustomerHash = function () {
    return postMessageToNativeApp({
        type: 'GET_CUSTOMER_HASH',
    });
};
var setTrackingProperty = function (system, name, value) {
    return postMessageToNativeApp({
        type: 'SET_TRACKING_PROPERTY',
        payload: {
            system: system,
            name: name,
            value: value,
        },
    }).catch(function () {
        // do nothing
    });
};

/**
 * This method is used by webapp to request the native app to renew current session
 * When webapp (running inside a webview) receives a 401 api response from server, uses this
 * bridge method to renew the session.
 */
var renewSession = function (oldAccessToken) {
    return postMessageToNativeApp({
        type: 'RENEW_SESSION',
        payload: { accessToken: oldAccessToken || null },
    }).then(function (_a) {
        var accessToken = _a.accessToken;
        return accessToken;
    });
};
/**
 * This method is used to listen for session renewals made by native app. Whenever the native app
 * renews the session with the api, it should notify webpp with this message.
 * This message is initiated by native app.
 */
var onSessionRenewed = function (handler) {
    return listenToNativeMessage('SESSION_RENEWED', function (_a) {
        var accessToken = _a.accessToken;
        return handler(accessToken);
    });
};

export { CD_EVENT_VALUE, CD_NOVUM_UID, CD_WEBAPP_INSTALLED, attachToEmail, checkPermissionStatus, createCalendarEvent, dismiss, fetch, fetchContactsByPhone, getAppMetadata, getCustomerHash, getDiskSpaceInfo, getEsimInfo, highlightNavigationTab, internalNavigation, isABTestingAvailable, isWebViewBridgeAvailable, logEcommerceEvent, logEvent, logTiming, nativeAlert, nativeConfirm, nativeMessage, notifyPageLoaded, onNativeEvent, onSessionRenewed, renewSession, reportStatus, requestContact, requestDeviceImei, requestSimIcc, requestSimImsi, requestVibration, setActionBehavior, setCustomerHash, setScreenName, setTrackingProperty, setUserProperty, setWebViewTitle, share, updateNavigationBar };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@tef-novum/webview-bridge/dist/webview-bridge.mjs
// module id = null
// module chunks = 